export interface NavEntry {
  label: string;
  href: string;
  icon: string;
}

export interface ProfileNavEntry extends NavEntry {
  dividerBefore?: boolean;
  danger?: boolean;
}

export interface MainNavFlags {
  hasDashboard: boolean;
  hasEmployeeManagement: boolean;
  hasScheduling: boolean;
  hasAttendanceAdmin: boolean;
  hasTimecards: boolean;
}

export interface MyRecordsFlags {
  canShowMessages: boolean;
  hasMyLicenses: boolean;
  hasMarketplaceProposals: boolean;
}

export interface ProfileMenuFlags {
  hasSettings: boolean;
  hasLeavePolicies: boolean;
  hasHolidayManagement: boolean;
  hasIncidentManagement: boolean;
  showSecurityCommandControlLogLink: boolean;
  showFeatureAccessLink: boolean;
  hasTaskManagement: boolean;
  hasMessageManagement: boolean;
  hasJobMonitor: boolean;
  hasJobApplicantTracking: boolean;
  hasScheduling: boolean;
  hasQaChecks?: boolean;
}

export const MY_RECORDS_PATHS = [
  '/my-attendances',
  '/my-schedules',
  '/shift-marketplace',
  '/my-tasks',
  '/messages',
  '/my-messages',
  '/my-trainings',
  '/my-licenses',
  '/my-holidays',
  '/my-incidents',
  '/ai-assistant',
];

export function buildMainNavigation(flags: MainNavFlags): NavEntry[] {
  const items: NavEntry[] = [];
  if (flags.hasDashboard) {
    items.push({ label: 'Dashboard', href: '/', icon: '📊' });
  }
  if (flags.hasEmployeeManagement) {
    items.push({ label: 'Employees', href: '/employees', icon: '👥' });
  }
  if (flags.hasScheduling) {
    items.push({ label: 'Scheduling', href: '/scheduling', icon: '📅' });
  }
  if (flags.hasAttendanceAdmin) {
    items.push({ label: 'Attendance', href: '/attendance', icon: '⏰' });
  }
  if (flags.hasTimecards) {
    items.push({ label: 'Timecards', href: '/timecards', icon: '📝' });
  }
  return items;
}

export function buildMyRecordsItems(flags: MyRecordsFlags): NavEntry[] {
  const items: NavEntry[] = [
    { label: 'My Attendances', href: '/my-attendances', icon: '⏱️' },
    { label: 'My Schedules', href: '/my-schedules', icon: '📅' },
  ];
  if (flags.hasMarketplaceProposals) {
    items.push({ label: 'Marketplace Proposals', href: '/shift-marketplace', icon: '🔄' });
  }
  items.push({ label: 'My Tasks', href: '/my-tasks', icon: '✅' });
  if (flags.canShowMessages) {
    items.push({ label: 'Messages', href: '/messages', icon: '💬' });
  }
  items.push(
    { label: 'My Trainings', href: '/my-trainings', icon: '🎓' },
  );
  if (flags.hasMyLicenses) {
    items.push({ label: 'My Compliance', href: '/my-licenses', icon: '📋' });
  }
  items.push(
    { label: 'My Holidays', href: '/my-holidays', icon: '🏖️' },
    { label: 'My Incidents', href: '/my-incidents', icon: '⚠️' },
    { label: 'AI Assistant', href: '/ai-assistant', icon: '🤖' },
  );
  return items;
}

export function buildProfileMenuItems(flags: ProfileMenuFlags): ProfileNavEntry[] {
  const items: ProfileNavEntry[] = [];
  if (flags.hasSettings) {
    items.push({ label: 'Settings', href: '/settings', icon: '⚙️' });
  }
  if (flags.hasQaChecks) {
    items.push({ label: 'QA Checks', href: '/qa-checks', icon: '✅' });
  }
  if (flags.hasLeavePolicies) {
    items.push({ label: 'Leave Policies', href: '/leave-policies', icon: '📄' });
  }
  items.push({ label: 'My Account', href: '/my-account', icon: '👤' });
  if (flags.hasHolidayManagement) {
    items.push({ label: 'Holiday Admin', href: '/holiday-admin', icon: '🏖️' });
  }
  if (flags.hasIncidentManagement) {
    items.push({ label: 'Incident Admin', href: '/incident-admin', icon: '⚠️' });
  }
  if (flags.hasScheduling) {
    items.push({ label: 'Marketplace Approvals', href: '/shift-marketplace/approvals', icon: '🔄' });
  }
  if (flags.showSecurityCommandControlLogLink) {
    items.push({
      label: 'Security Command and Control Log',
      href: '/security-command-control-log',
      icon: '🛡️',
    });
  }
  if (flags.showFeatureAccessLink) {
    items.push({
      label: 'Feature Access',
      href: '/settings?section=feature-access',
      icon: '🎛️',
    });
  }
  if (flags.hasTaskManagement) {
    items.push({ label: 'Task Admin', href: '/task-admin', icon: '✅' });
  }
  if (flags.hasMessageManagement) {
    items.push({ label: 'Message Audit', href: '/message-admin', icon: '📋' });
  }
  if (flags.hasJobMonitor) {
    items.push({ label: 'Job Admin', href: '/job-monitor', icon: '⚡' });
  }
  if (flags.hasJobApplicantTracking) {
    items.push({ label: 'Job Applicant Tracking', href: '/job-applicant-tracking', icon: '💼' });
  }
  return items;
}

export const reportsNavItem: NavEntry = {
  label: 'Reports',
  href: '/reports',
  icon: '📈',
};

export const myRecordsNavTrigger: NavEntry = {
  label: 'My Records',
  href: '#',
  icon: '📋',
};

export const signOutEntry: ProfileNavEntry = {
  label: 'Sign Out',
  href: '#sign-out',
  icon: '🚪',
  dividerBefore: true,
  danger: true,
};
